// /src/pages/Prequalify.js
import { useState } from "react";

const LOAN_TYPES = [
  "Fix & Flip",
  "Ground Up Construction",
  "Rental Property",
  "Bridge Loan",
  "Term Loan",
  "DSCR Loan",
  "Not sure yet",
];

export default function Prequalify() {
  const params = new URLSearchParams(window.location.search);
  const leadId = params.get("lead");

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    projectType: "",
    hasProperty: "",
    creditScore: "",
    hasLlc: "",
    timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch("/api/prequal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId, ...form }),
      });
      const data = await res.json();
      setOutcome(data.outcome);
    } catch {
      setOutcome("error");
    }
    setSubmitting(false);
  };

  if (outcome) return <OutcomeScreen outcome={outcome} />;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 40, maxWidth: 520, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>

        {/* Header */}
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 8 }}>Step {step} of 5</div>
          <div style={{ height: 4, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(step / 5) * 100}%`, background: "#1d4ed8", borderRadius: 4, transition: "width 0.3s" }} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 24, marginBottom: 4 }}>
            Quick Pre-Qualification
          </h1>
          <p style={{ fontSize: 14, color: "#64748b" }}>Takes about 2 minutes — no credit pull required.</p>
        </div>

        {/* Steps */}
        {step === 1 && (
          <Question label="What type of loan are you looking for?">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {LOAN_TYPES.map(t => (
                <OptionButton key={t} label={t} selected={form.projectType === t} onClick={() => { set("projectType", t); setStep(2); }} />
              ))}
            </div>
          </Question>
        )}

        {step === 2 && (
          <Question label="Do you have a specific property in mind?">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[["yes", "Yes, I have a property identified"], ["looking", "Still looking"], ["no", "No property yet"]].map(([val, label]) => (
                <OptionButton key={val} label={label} selected={form.hasProperty === val} onClick={() => { set("hasProperty", val); setStep(3); }} />
              ))}
            </div>
          </Question>
        )}

        {step === 3 && (
          <Question label="What's your approximate credit score?">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[["750+", "750 or above"], ["700-749", "700 – 749"], ["650-699", "650 – 699"], ["below650", "Below 650"]].map(([val, label]) => (
                <OptionButton key={val} label={label} selected={form.creditScore === val} onClick={() => { set("creditScore", val === "750+" ? 760 : val === "700-749" ? 720 : val === "650-699" ? 670 : 600); setStep(4); }} />
              ))}
            </div>
          </Question>
        )}

        {step === 4 && (
          <Question label="Do you have an LLC?">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[["yes", "Yes, I have an active LLC"], ["willing", "No, but I'm willing to create one"], ["no", "No, and I prefer not to"]].map(([val, label]) => (
                <OptionButton key={val} label={label} selected={form.hasLlc === val} onClick={() => { set("hasLlc", val); setStep(5); }} />
              ))}
            </div>
          </Question>
        )}

        {step === 5 && (
          <Question label="What's your timeline to get funded?">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[["asap", "ASAP — ready to move now"], ["30days", "Within 30 days"], ["60days", "1–3 months"], ["exploring", "Just exploring options"]].map(([val, label]) => (
                <OptionButton key={val} label={label} selected={form.timeline === val} onClick={() => set("timeline", val)} />
              ))}
            </div>

            {/* Communication Consent Disclaimer */}
            <div style={{
              marginTop: 24,
              padding: "14px 16px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}>
              <input
                type="checkbox"
                id="consent"
                checked
                readOnly
                style={{
                  marginTop: 2,
                  width: 16,
                  height: 16,
                  accentColor: "#1d4ed8",
                  flexShrink: 0,
                  cursor: "default",
                }}
              />
              <label htmlFor="consent" style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5, cursor: "default" }}>
                <span style={{ fontWeight: 600, color: "#475569", display: "block", marginBottom: 2 }}>
                  Communication Consent
                </span>
                By submitting this form, I consent to receive calls, texts, chat messages and emails from Propertunity Capital regarding real estate financing options. Message and data rates may apply. Reply STOP to opt out at any time.
              </label>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!form.timeline || submitting}
              style={{ marginTop: 16, width: "100%", padding: "14px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: !form.timeline || submitting ? 0.5 : 1 }}>
              {submitting ? "Submitting..." : "Submit & See My Results →"}
            </button>
          </Question>
        )}
      </div>
    </div>
  );
}

function Question({ label, children }) {
  return (
    <div>
      <p style={{ fontSize: 17, fontWeight: 600, color: "#0f172a", marginBottom: 20 }}>{label}</p>
      {children}
    </div>
  );
}

function OptionButton({ label, selected, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: "12px 16px", borderRadius: 8, border: `2px solid ${selected ? "#1d4ed8" : "#e2e8f0"}`,
      background: selected ? "#eff6ff" : "#fff", color: selected ? "#1d4ed8" : "#0f172a",
      fontSize: 14, fontWeight: selected ? 600 : 400, cursor: "pointer", textAlign: "left", transition: "all 0.15s",
    }}>
      {label}
    </button>
  );
}

function OutcomeScreen({ outcome }) {
  if (outcome === "qualified") return (
    <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 40, maxWidth: 480, width: "100%", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#15803d", marginBottom: 12 }}>You're Pre-Qualified!</h2>
        <p style={{ color: "#475569", marginBottom: 32 }}>Excellent news! Book your free 30-minute call with a loan specialist to get your exact rates and terms.</p>
        <a href="https://calendly.com/larisa-mathur-propertunitycapital/30min"
          style={{ display: "inline-block", background: "#16a34a", color: "#fff", padding: "14px 32px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 16 }}>
          Book Your Call →
        </a>
        <p style={{ marginTop: 16, fontSize: 13, color: "#94a3b8" }}>Check your email — we've sent you a confirmation with next steps.</p>
      </div>
    </div>
  );

  if (outcome === "warm") return (
    <div style={{ minHeight: "100vh", background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 40, maxWidth: 480, width: "100%", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>👋</div>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#d97706", marginBottom: 12 }}>Thanks for Applying!</h2>
        <p style={{ color: "#475569", marginBottom: 32 }}>One of our specialists will reach out shortly to discuss your options. You can also book a call directly below.</p>
        <a href="https://calendly.com/larisa-mathur-propertunitycapital/30min"
          style={{ display: "inline-block", background: "#1d4ed8", color: "#fff", padding: "14px 32px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 16 }}>
          Schedule a Call →
        </a>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 40, maxWidth: 480, width: "100%", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>💙</div>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Thank You</h2>
        <p style={{ color: "#475569" }}>We appreciate your interest. Unfortunately we're not the right fit at this time, but we encourage you to reach back out once your credit score reaches 650+.</p>
      </div>
    </div>
  );
}